import { Component } from '@theme/component';

/**
 * @typedef {object} CustomBuilderRefs
 * @property {HTMLSelectElement} variant
 * @property {HTMLInputElement} quantity
 * @property {HTMLInputElement} upload
 * @property {HTMLImageElement} artPreview
 * @property {HTMLImageElement | undefined} bandImage
 * @property {HTMLElement} price
 * @extends Component<CustomBuilderRefs>
 */
class MsCustomBuilder extends Component {
  #previewUrl;

  connectedCallback() {
    super.connectedCallback();
    this.changeVariant();
  }

  updatedCallback() {
    super.updatedCallback();
    this.changeVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#releasePreview();
  }

  /** Keep the selected real Shopify variant's price, image and minimum quantity aligned. */
  changeVariant() {
    const option = this.refs.variant?.selectedOptions[0];
    if (!option) return;
    this.refs.price.textContent = option.dataset.price || '';
    this.refs.quantity.value = option.dataset.quantity || '1';
    if (option.dataset.image && this.refs.bandImage) {
      this.refs.bandImage.removeAttribute('srcset');
      this.refs.bandImage.src = option.dataset.image;
    }
  }

  /** Preview artwork locally. The native multipart form uploads it only when the shopper adds the band. */
  previewArtwork() {
    const { upload, artPreview } = this.refs;
    this.#releasePreview();
    artPreview.hidden = true;
    artPreview.removeAttribute('src');
    upload.setCustomValidity('');
    const file = upload.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      upload.setCustomValidity(upload.dataset.typeError || 'Choose a JPG, PNG or WebP image.');
    } else if (file.size > 20 * 1024 * 1024) {
      upload.setCustomValidity(upload.dataset.sizeError || 'Choose an image smaller than 20 MB.');
    }
    if (!upload.checkValidity()) {
      upload.reportValidity();
      return;
    }
    this.#previewUrl = URL.createObjectURL(file);
    artPreview.src = this.#previewUrl;
    artPreview.hidden = false;
  }

  #releasePreview() {
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    this.#previewUrl = undefined;
  }
}
if (!customElements.get('ms-custom-builder')) customElements.define('ms-custom-builder', MsCustomBuilder);
